import React, { useState } from "react";
import { Clock, Film } from "lucide-react";

/**
 * ComingSoonCard — Netflix/Prime-style card for upcoming productions.
 * Shows a centered background + title at rest.
 * On hover: slightly scales up and fades in badges + description + CTA.
 */
export default function ComingSoonCard({
  testId = "coming-soon-card",
  bgImage,
  titleImage,
  titleText,
  tagline,
  description,
  badges = [],
  ageRating,
  focal = "center",
}) {
  const [hover, setHover] = useState(false);

  return (
    <div
      data-testid={testId}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      className="group relative w-full rounded-2xl overflow-hidden border border-white/10 bg-[color:var(--ns-card)] transition-all duration-500 ease-out hover:z-20"
      style={{
        aspectRatio: "16 / 9",
        transform: hover ? "scale(1.045)" : "scale(1)",
        boxShadow: hover ? "0 30px 70px -20px rgba(0,0,0,0.8), 0 0 0 1px rgba(255,59,29,0.35)" : "0 10px 24px -16px rgba(0,0,0,0.6)",
      }}
    >
      <img
        src={bgImage}
        alt={titleText || "Prochainement"}
        className="absolute inset-0 w-full h-full object-cover transition-transform duration-700"
        style={{ objectPosition: focal, transform: hover ? "scale(1.08)" : "scale(1)" }}
      />

      {/* Resting-state gradient: center-vignette so the title stays readable */}
      <div
        className="absolute inset-0 transition-opacity duration-500"
        style={{
          background:
            "radial-gradient(ellipse at center, rgba(0,0,0,0.15) 0%, rgba(0,0,0,0.55) 60%, rgba(0,0,0,0.85) 100%)",
          opacity: hover ? 0 : 1,
        }}
      />

      {/* Hover-state gradient (darker, bottom-weighted for text) */}
      <div
        className="absolute inset-0 transition-opacity duration-500"
        style={{
          background:
            "linear-gradient(180deg, rgba(0,0,0,0.25) 0%, rgba(0,0,0,0.55) 55%, rgba(0,0,0,0.92) 100%)",
          opacity: hover ? 1 : 0,
        }}
      />

      {/* Age rating pill — always visible */}
      {ageRating && (
        <div className="absolute top-3 right-3 z-10">
          <span className="font-mono-mini text-[11px] px-2 py-1 bg-black/80 border border-white/30 text-white uppercase tracking-widest rounded">
            {ageRating}
          </span>
        </div>
      )}

      {/* Resting-state: centered logo/title */}
      <div
        className="absolute inset-0 flex items-center justify-center p-6 transition-all duration-500"
        style={{
          opacity: hover ? 0 : 1,
          transform: hover ? "translateY(-10px) scale(0.95)" : "translateY(0) scale(1)",
        }}
      >
        {titleImage ? (
          <img
            src={titleImage}
            alt={titleText || "Title"}
            className="max-w-[70%] max-h-[55%] object-contain drop-shadow-[0_6px_24px_rgba(255,59,29,0.4)]"
          />
        ) : (
          <h3 className="font-display text-3xl sm:text-4xl text-white uppercase tracking-tight text-center drop-shadow-[0_6px_24px_rgba(0,0,0,0.9)]">
            {titleText}
          </h3>
        )}
      </div>

      {/* Hover reveal: title top-left + info bottom */}
      <div
        className="absolute inset-0 flex flex-col justify-between p-5 sm:p-6 transition-all duration-500"
        style={{ opacity: hover ? 1 : 0 }}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-mono-mini text-[10px] px-2.5 py-1 border border-[color:var(--ns-accent)] text-[color:var(--ns-accent)] uppercase tracking-[0.3em] flex items-center gap-1.5">
              <Film size={11} /> Kinebe
            </span>
            <span className="font-mono-mini text-[10px] px-2.5 py-1 border border-white/20 text-white uppercase tracking-[0.3em] flex items-center gap-1.5">
              <Clock size={11} /> Bientôt
            </span>
          </div>
        </div>

        <div className="space-y-3">
          {tagline && (
            <div className="font-mono-mini text-[10px] uppercase tracking-[0.4em] text-zinc-400">
              {tagline}
            </div>
          )}

          {titleImage ? (
            <img
              src={titleImage}
              alt={titleText || "Title"}
              className="max-h-[60px] sm:max-h-[72px] object-contain drop-shadow-[0_4px_18px_rgba(255,59,29,0.4)]"
            />
          ) : (
            <h3 className="font-display text-2xl sm:text-3xl text-white uppercase tracking-tight leading-none">
              {titleText}
            </h3>
          )}

          {badges.length > 0 && (
            <div className="flex items-center gap-1.5 flex-wrap">
              {badges.map((b) => (
                <span
                  key={b}
                  className="font-mono-mini text-[9px] px-2 py-0.5 bg-white/10 border border-white/15 text-zinc-100 uppercase tracking-[0.25em]"
                >
                  {b}
                </span>
              ))}
            </div>
          )}

          {description && (
            <p className="text-xs sm:text-sm text-zinc-200 leading-relaxed line-clamp-3 max-w-xl">
              {description}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
